import { useCallback } from "react";
import logger from "../utils/logger";

/**
 * Hook for managing installation-related handler functions
 * Extracted from AppContent.js (Phase 1 Day 45)
 */
export function useInstallationHandlers({
  allDeviceStatesLoaded,
  devices,
  deviceStates,
  installationId,
  organizationId,
  setIsInstallationActionsOpen,
  setInstallationManagementOpen,
  setIsOrganizationManagementOpen,
  setOrgSettings,
  triggerInstallationSync,
  invalidateInstallationSyncCache,
}) {
  // Handler to open Installation Actions modal (only when device states are loaded)
  const handleOpenInstallationActions = useCallback(() => {
    // OPTION 2: Check if we need to sync before opening modal
    if (!allDeviceStatesLoaded) {
      logger.log(
        `[handleOpenInstallationActions] ⏳ Device states not fully loaded (${
          Object.keys(deviceStates).length
        }/${devices?.length || 0}), triggering state-only sync...`
      );

      // Open modal immediately (will show loading state)
      setIsInstallationActionsOpen(true);

      // Trigger installation-wide sync with stateOnly=true (Option 2 + Option 4)
      // This skips credential fetching for faster sync
      // FORCE=true to bypass cache since we need all device states, not just credentials
      triggerInstallationSync("Installation Actions modal opened", true, true);

      return;
    }

    logger.log(
      `[handleOpenInstallationActions] ✅ All device states already loaded, opening modal`
    );
    setIsInstallationActionsOpen(true);
  }, [
    allDeviceStatesLoaded,
    devices,
    deviceStates,
    triggerInstallationSync,
    setIsInstallationActionsOpen,
  ]);

  const handleOpenInstallationManagement = useCallback(
    (organizationId = null) => {
      logger.log(
        `[handleOpenInstallationManagement] Opening Installation Management modal`,
        { organizationId }
      );

      // OPTION 2: Trigger sync if needed before opening modal
      if (!allDeviceStatesLoaded) {
        logger.log(
          `[handleOpenInstallationManagement] Device states not loaded, triggering sync...`
        );
        triggerInstallationSync("Installation Management modal opened");
      }

      setInstallationManagementOpen(true, organizationId);
    },
    [
      allDeviceStatesLoaded,
      triggerInstallationSync,
      setInstallationManagementOpen,
    ]
  );

  // FIX #2: Handler to retry sync from Installation Actions modal
  const handleRetryInstallationSync = useCallback(() => {
    logger.log(
      `[handleRetryInstallationSync] User requested sync retry from modal`
    );
    // Force sync by invalidating cache first
    invalidateInstallationSyncCache(installationId);
    triggerInstallationSync("User retry from Installation Actions modal", true);
  }, [
    installationId,
    triggerInstallationSync,
    invalidateInstallationSyncCache,
  ]);

  const handleOpenOrganizationManagement = useCallback(() => {
    logger.log(
      `[handleOpenOrganizationManagement] Opening Organization Management modal`
    );
    setIsOrganizationManagementOpen(true);
  }, [setIsOrganizationManagementOpen]);

  const handleSettingsUpdated = useCallback(
    (newSettings) => {
      logger.log(
        "[handleSettingsUpdated] Settings updated, applying immediately",
        newSettings
      );

      // Immediate local update
      setOrgSettings(newSettings);

      // Update cache with new timestamp
      const cacheKey = `orgSettings_${organizationId}`;
      localStorage.setItem(
        cacheKey,
        JSON.stringify({ settings: newSettings, timestamp: Date.now() })
      );

      // This will trigger storage events in other tabs
    },
    [organizationId, setOrgSettings]
  );

  return {
    handleOpenInstallationActions,
    handleOpenInstallationManagement,
    handleRetryInstallationSync,
    handleOpenOrganizationManagement,
    handleSettingsUpdated,
  };
}
